import type { Dialogue } from '../../shared/protocol.ts';
import { $, el } from '../common/dom.ts';
import { isVoiced } from '../common/voice-synth.ts';
import type { SoundBoard } from './sound.ts';
import { TypedText } from './text-effects.ts';

const TYPE_MS_PER_CHAR = 6;
/** Écart minimal entre deux syllabes de la voix (ms) : le texte s'écrit bien plus vite qu'on ne parle. */
const VOICE_GAP_MS = 70;
/** Volume de la musique tant qu'une boîte de dialogue est ouverte. */
const DIALOGUE_MUSIC = 0.6;
/** Identifiant des textes de quête affichés par le client lui-même (aucune réponse au serveur). */
const NOTE_ID = -1;

/**
 * Boîte de dialogue d'un PNJ, façon RPG : texte écrit lettre par lettre, page par page
 * (Espace, Entrée ou toucher pour avancer), puis choix éventuels.
 */
export class DialogueBox {
  private readonly root = $('#dialogue');
  private readonly speaker = $('.dialogue-speaker', this.root);
  private readonly text = $('.dialogue-text', this.root);
  private readonly choices = $('.dialogue-choices', this.root);
  private readonly next = $('.dialogue-next', this.root);
  private readonly onReply: (id: number, choice: number | null) => void;
  private readonly sounds: SoundBoard;
  private dialogue: Dialogue | null = null;
  private page = 0;
  private typing: ReturnType<typeof setTimeout> | undefined;
  /** Page en cours d'écriture, avec ses effets ({boom}, {pause}…) et ses styles ({tremble}…). */
  private typed: TypedText | null = null;
  /** Unités déjà écrites de la page en cours. */
  private written = 0;
  private selected = 0;
  /** Dernière syllabe de la voix jouée (performance.now()). */
  private spokeAt = 0;
  /** Textes de quête en attente (étape atteinte…), une liste de pages chacun : voir showNote. */
  private readonly notes: string[][] = [];

  constructor(onReply: (id: number, choice: number | null) => void, sounds: SoundBoard) {
    this.onReply = onReply;
    this.sounds = sounds;
    this.root.addEventListener('click', (event) => {
      if (!(event.target as Element).closest('.dialogue-choices')) this.advance();
    });
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  /** Haut de la boîte à l'écran, en pixels (le bas de l'écran si elle est fermée) : ce qui est dessous est caché. */
  get top(): number {
    return this.root.hidden ? innerHeight : this.root.getBoundingClientRect().top;
  }

  show(dialogue: Dialogue): void {
    // Un texte de quête interrompu par un PNJ se relira après lui.
    if (this.dialogue?.id === NOTE_ID && dialogue.id !== NOTE_ID) this.notes.unshift(this.dialogue.pages);
    // Une page encore en cours d'écriture (dialogue précédent) ne doit plus avancer.
    clearTimeout(this.typing);
    this.typing = undefined;
    this.dialogue = dialogue;
    this.page = 0;
    this.speaker.textContent = dialogue.speaker;
    this.root.hidden = false;
    this.sounds.play('select');
    this.sounds.duck('music', DIALOGUE_MUSIC);
    this.typePage();
  }

  /** Clavier : Espace / Entrée pour avancer, flèches et chiffres pour les choix. */
  handleKey(event: KeyboardEvent): void {
    const choiceCount = this.choiceButtons().length;
    if (choiceCount && (event.code === 'ArrowUp' || event.code === 'ArrowLeft')) this.select((this.selected + choiceCount - 1) % choiceCount);
    else if (choiceCount && (event.code === 'ArrowDown' || event.code === 'ArrowRight')) this.select((this.selected + 1) % choiceCount);
    else if (choiceCount && /^Digit[1-9]$/.test(event.code)) this.choose(Number(event.code.slice(5)) - 1);
    else if (event.code === 'Space' || event.key === 'Enter') this.advance();
    else return;
    event.preventDefault();
  }

  /** Termine la page en cours, passe à la suivante, ou valide / ferme à la dernière. */
  private advance(): void {
    const { dialogue } = this;
    if (!dialogue) return;
    if (this.typing !== undefined) return this.skipTyping();
    const choiceButtons = this.choiceButtons();
    if (choiceButtons.length) return this.choose(this.selected);
    if (this.page < dialogue.pages.length - 1) {
      this.page++;
      this.sounds.play('select');
      return this.typePage();
    }
    this.reply(null);
  }

  private typePage(): void {
    const typed = new TypedText(this.dialogue?.pages[this.page] ?? '', this.sounds);
    this.typed = typed;
    this.written = 0;
    this.text.textContent = '';
    this.choices.hidden = true;
    this.next.hidden = true;
    this.typing = setTimeout(() => this.typeNext(), TYPE_MS_PER_CHAR + typed.reach(0));
  }

  /** Écrit la lettre suivante ; une {pause} ou un fondu au {noir} retardent celle d'après. */
  private typeNext(): void {
    const { typed } = this;
    if (!typed) return;
    this.written++;
    this.text.replaceChildren(...typed.render(this.written));
    this.speak(typed.charAt(this.written - 1));
    const wait = typed.reach(this.written);
    if (this.written < typed.length) this.typing = setTimeout(() => this.typeNext(), TYPE_MS_PER_CHAR + wait);
    else if (wait) this.typing = setTimeout(() => this.finishTyping(), wait);
    else this.finishTyping();
  }

  /** La voix de celui qui parle dit la lettre qui vient de s'écrire, si la précédente syllabe est finie. */
  private speak(char: string | null): void {
    const voice = this.dialogue?.voice;
    const now = performance.now();
    if (!voice || char === null || !isVoiced(char) || now - this.spokeAt < VOICE_GAP_MS) return;
    this.spokeAt = now;
    this.sounds.speak(voice, char);
  }

  /** Avance rapide : la page s'écrit d'un coup, mais une {pause} se joue jusqu'au bout et arrête l'avance. */
  private skipTyping(): void {
    const { typed } = this;
    if (!typed) return this.finishTyping();
    if (typed.pauseLeft > 0) return;
    clearTimeout(this.typing);
    this.written = typed.skip();
    const pause = typed.pauseLeft;
    if (!pause) return this.finishTyping();
    this.text.replaceChildren(...typed.render(this.written));
    const resume = this.written < typed.length ? () => this.typeNext() : () => this.finishTyping();
    this.typing = setTimeout(resume, pause);
  }

  private finishTyping(): void {
    clearTimeout(this.typing);
    this.typing = undefined;
    // Page écrite d'un coup (le joueur l'a passée) : les effets restants se jouent quand même.
    this.typed?.reach(Infinity);
    if (this.typed) this.text.replaceChildren(...this.typed.render());
    const { dialogue } = this;
    const lastPage = dialogue !== null && this.page === dialogue.pages.length - 1;
    if (lastPage && dialogue.choices?.length) this.showChoices(dialogue.choices);
    else this.next.hidden = false;
  }

  private showChoices(labels: string[]): void {
    this.choices.replaceChildren(...labels.map((label, index) => {
      const button = el('button', { textContent: label, className: index === 0 ? '' : 'secondary' });
      button.addEventListener('click', () => this.choose(index));
      return button;
    }));
    this.choices.hidden = false;
    this.select(0);
  }

  private choiceButtons(): HTMLButtonElement[] {
    return this.choices.hidden ? [] : [...this.choices.querySelectorAll('button')];
  }

  private select(index: number): void {
    this.selected = index;
    this.choiceButtons().forEach((button, i) => button.classList.toggle('selected', i === index));
  }

  private choose(index: number): void {
    if (index >= 0 && index < this.choiceButtons().length) this.reply(index);
  }

  /** Met de côté un texte de quête à lire : il s'affiche dès que l'écran est libre (voir showNote). */
  queueNote(pages: string[]): void {
    if (pages.length) this.notes.push(pages);
  }

  /** Affiche le prochain texte de quête en attente, si la boîte est libre : le joueur le lit et le ferme lui-même. */
  showNote(): void {
    const pages = this.dialogue ? undefined : this.notes.shift();
    if (pages) this.show({ id: NOTE_ID, speaker: 'Quête', voice: null, pages, choices: null, watchOnly: true });
  }

  /** Ferme la boîte sans répondre et oublie les textes de quête en attente (déconnexion). */
  close(): void {
    this.notes.length = 0;
    this.hide();
  }

  private hide(): void {
    clearTimeout(this.typing);
    this.typing = undefined;
    this.typed = null;
    this.dialogue = null;
    this.root.hidden = true;
    this.sounds.duck('music', 1);
  }

  /** Ferme la boîte et envoie la réponse au PNJ (rien pour une réplique seulement regardée, en coop). */
  private reply(choice: number | null): void {
    const { dialogue } = this;
    this.hide();
    if (dialogue && !dialogue.watchOnly) this.onReply(dialogue.id, choice);
  }

  /** Ferme la réplique ouverte, sans réponse (le serveur est passé à la suite). */
  dismiss(): void {
    if (this.dialogue && this.dialogue.id !== NOTE_ID) this.hide();
  }
}
